import { Button, Multiselect, Select, SelectOption } from "@rio-cloud/rio-uikit";
import { useNavigate, useLocation } from "react-router";
import { useEffect, useMemo, useState } from "react";
import { useIntl } from "react-intl";
import { resolveAssignmentSelection } from "../constants";
import { getSortOptionId, SortOptionsSupport } from "./SortOptionsSupport";

interface Props {
    handleSearch: (text: string) => void;
    handleSupportType: (subject: string) => void;
    handleDn: (text: string) => void;
    handleResponsible: (text: string) => void;
    handleRegional: (value: string) => void;
    handleSort: (optionId: string) => void;
    optionsRegionals: SelectOption[];
    optionsAssigned: SelectOption[];
    search?: string;
    dn?: string;
    responsible?: string;
    regional?: string;
    sortBy?: string;
    sortDirection?: string;
    supportType?: string;
    handleAssignedChange: (values: string[]) => void;
    assignedSelection: string[];
}

const ToollBar = ({
    handleSearch,
    handleSupportType,
    handleDn,
    handleResponsible,
    optionsRegionals,
    optionsAssigned,
    handleRegional,
    handleSort,
    search,
    dn,
    regional,
    responsible,
    sortBy,
    sortDirection,
    supportType,
    handleAssignedChange,
    assignedSelection,
}: Props) => {
    const intl = useIntl();
    const [reg, setReg] = useState('');
    const navigate = useNavigate();
    const location = useLocation();

    const handleChange = (value: string) => {
        handleSearch(value)
    };

    const handleChangeSupportType = (sub?: string) => {
        if (!sub) return;
        handleSupportType(sub);
    }

    const handleChangeDn = (value: string) => {
        handleDn(value)
    };

    const handleChangeResponsible = (value: string) => {
        handleResponsible(value)
    };

    const handleRegionalValue = (value?: string) => {
        if (!value) return;

        handleRegional(value)
    }

    const handleChangeAssigned = (selected: string[]) => {
        const nextSelection = resolveAssignmentSelection(selected, assignedSelection);
        handleAssignedChange(nextSelection);
    }

    const handleChangeSort = (optionId?: string) => {
        if (!optionId || optionId === 'HEADER') return;
        handleSort(optionId);
    };

    const goToNewSupportCall = () => {
        navigate({
            pathname: location.pathname + '/new-support-call',
        });
    };

    const supportTypeOptions = useMemo(() => [
        {
            id: 'TECHNICAL_SUPPORT',
            label: intl.formatMessage({ id: 'intl-msg:tas.support.toolbar.supportType.technical' }),
        }, {
            id: 'VOLKSTOTAL',
            label: intl.formatMessage({ id: 'intl-msg:tas.support.toolbar.supportType.volkstotal' }),
        }, {
            id: 'all',
            label: intl.formatMessage({ id: 'intl-msg:tas.support.toolbar.supportType.all' }),
        },
    ], [intl]);

    useEffect(() => {
        if (regional) {
            setReg(regional)
        }
    }, [regional])

    return (
        <div className="margin-top-20">
            <div>
                <span className="text-medium text-size-18" style={{ color: "#2A3740" }}>
                    {intl.formatMessage({ id: 'intl-msg:tas.support.toolbar.title' })}
                </span>
            </div>

            <div className="table-toolbar">
                <div className="table-toolbar-container">
                    <div className="table-toolbar-group flex-1-1 align-items-end flex-nowrap">
                        <div className="table-toolbar-column table-toolbar-column-spacer">
                            <Button onClick={goToNewSupportCall} bsStyle={Button.PRIMARY} iconName='rioglyph-plus' style={{ width: 150 }}>
                                {intl.formatMessage({ id: 'intl-msg:tas.support.toolbar.newRequest' })}
                            </Button>
                        </div>

                        <div className="table-toolbar-column table-toolbar-column-spacer w-full">
                            <input
                                type="text"
                                className="form-control"
                                placeholder={intl.formatMessage({ id: 'intl-msg:tas.support.toolbar.searchPlaceholder' })}
                                id="chassisSearch"
                                value={search}
                                onChange={(event) => handleChange(event.target.value)}
                                maxLength={17}
                                style={{ height: "34px", minHeight: "34px", maxHeight: "34px", lineHeight: "34px" }}
                            />
                        </div>

                        <div className="table-toolbar-column table-toolbar-column-spacer w-full">
                            <label htmlFor="regional">{intl.formatMessage({ id: 'intl-msg:tas.support.toolbar.regional' })}</label>
                            <Select
                                id="regional"
                                placeholder={intl.formatMessage({ id: 'intl-msg:tas.support.toolbar.selectPlaceholder' })}
                                value={[reg]}
                                options={optionsRegionals}
                                useFilter
                                className="border"
                                noItemMessage={intl.formatMessage({ id: 'intl-msg:tas.support.toolbar.noSubjectFound' })}
                                onChange={(selected) => handleRegionalValue(selected?.id)}
                            />
                        </div>

                        <div className="table-toolbar-column table-toolbar-column-spacer w-full">
                            <label htmlFor="supportType">{intl.formatMessage({ id: 'intl-msg:tas.support.toolbar.supportType' })}</label>
                            <Select
                                id="supportType"
                                placeholder={intl.formatMessage({ id: 'intl-msg:tas.support.toolbar.selectPlaceholder' })}
                                value={[supportType || '']}
                                options={supportTypeOptions}
                                useFilter
                                className="border"
                                noItemMessage={intl.formatMessage({ id: 'intl-msg:tas.support.toolbar.noSubjectFound' })}
                                onChange={(selected) => handleChangeSupportType(selected?.id)}
                            />
                        </div>

                        <div className="table-toolbar-column table-toolbar-column-spacer w-full">
                            <label htmlFor="responsible">{intl.formatMessage({ id: 'intl-msg:tas.support.toolbar.responsible' })}</label>
                            <input
                                type="text"
                                className="form-control"
                                id="responsible"
                                value={responsible}
                                onChange={(event) => handleChangeResponsible(event.target.value)}
                                maxLength={17}
                                style={{ height: "34px", minHeight: "34px", maxHeight: "34px", lineHeight: "34px" }}
                            />
                        </div>
                        <div className="table-toolbar-column table-toolbar-column-spacer w-full">
                            <label htmlFor="dn">{intl.formatMessage({ id: 'intl-msg:tas.support.toolbar.dn' })}</label>
                            <input
                                type="text"
                                className="form-control"
                                id="dn"
                                value={dn}
                                onChange={(event) => handleChangeDn(event.target.value)}
                                maxLength={17}
                                style={{ height: "34px", minHeight: "34px", maxHeight: "34px", lineHeight: "34px" }}
                            />
                        </div>
                        <div className="table-toolbar-column w-full max-width-250">
                            <label htmlFor="assigned">{intl.formatMessage({ id: 'intl-msg:tas.support.toolbar.assignments' })}</label>
                            <Multiselect
                                id="assigned"
                                placeholder={intl.formatMessage({ id: 'intl-msg:tas.support.toolbar.selectPlaceholder' })}
                                value={assignedSelection}
                                options={optionsAssigned}
                                renderCounterMessage={(selectedAmount) =>
                                    intl.formatMessage(
                                        { id: 'intl-msg:tas.support.toolbar.selectedCount' },
                                        { count: selectedAmount },
                                    )
                                }
                                useFilter
                                className="border"
                                onChange={handleChangeAssigned}
                            />
                        </div>
                        <div className="table-toolbar-column table-toolbar-column-spacer w-full">
                            <label htmlFor="sortby">Ordenar por</label>
                            <Select
                                id="sortby"
                                placeholder={intl.formatMessage({ id: 'intl-msg:tas.support.toolbar.selectPlaceholder' })}
                                value={[getSortOptionId(sortBy || '', sortDirection || '')]}
                                options={SortOptionsSupport}
                                useFilter
                                className="border"
                                noItemMessage="Nenhuma opção de ordenação encontrada"
                                onChange={(selected) => handleChangeSort(selected?.id)}
                            />
                        </div>
                    </div>
                </div>

            </div>
        </div>
    )
}

export default ToollBar;
